import React, { memo, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  Box,
  makeStyles,
  Typography,
  IconButton,
  Checkbox,
  Divider
} from "@material-ui/core";
import SmallSearchBox from "../common/SmallSearchBox";
import {
  generateStopDataForXLSX,
  isEmptyObj,
  exportToXlsx,
  isNotEmptyRoute,
  mergeReloadStopsToMainRoute,
  getDateValue,
  distanceStr,
  checkIsFOJW
} from "../../utils/helpers";
import { getRouteListStatus } from "../../utils/routeListStatus";
import { setInfoDialog } from "../../store/actions/common";
import { connect } from "react-redux";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import RestoreIcon from "@material-ui/icons/Restore";
import ReactToPrint from "react-to-print";
import OptimizedTooltip from "../common/OptimizedTooltip";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import {
  reoptimizeRoutes,
  setSelectedRoutes
} from "../../store/actions/project";
import MagicIcon from "../icons/MagicIcon";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import LockIcon from "@material-ui/icons/Lock";
import { handleRoutesLock } from "../../store/actions/custom";
import { headerRow } from "../data/constants";
import PrintRoutes from "../project/PrintRoutes";
import PrintIcon from "@material-ui/icons/Print";
import { setLoadingRoutesList } from "../../store/actions/loading";
import ReplyIcon from "@material-ui/icons/Reply";
import PhotoCamera from "@material-ui/icons/PhotoCamera";
import { ReactComponent as NoPhotographyIcon } from "../../assets/images/icons/no-photography.svg";
import {
  reopenRoutes,
  setPodPhotoRequirementForRoutes
} from "../../store/actions/custom";
import { getCookie } from "../../utils/cookies";
import DateFnsUtils from "@date-io/date-fns";

const useStyles = makeStyles(theme => ({
  button: {
    marginLeft: theme.spacing(1.5)
  },
  iconButton: {
    "&.Mui-disabled": {
      opacity: 0.5
    }
  },
  toolbarWrapper: {
    height: 40,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 2, 2, 3),

    "& > div": {
      display: "flex",
      alignItems: "center"
    }
  },
  searchBox: {
    height: 40,
    transition: "width 0.3s",
    marginLeft: 0,
    width: "100%"
  },
  importButton: {
    marginLeft: theme.spacing(1.5),
    textTransform: "none",
    whiteSpace: "nowrap",
    fontSize: theme.typography.body2.fontSize,
    padding: theme.spacing(1, 2, 1, 1.5),
    border: "1px solid #DBDCE1",
    "& svg": {
      color: theme.palette.primary.main,
      marginRight: theme.spacing(1)
    }
  },
  toolbarActions: {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  checkboxWrapper: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(-1),
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main
    }
  },
  selectedText: {
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: "0.875rem",
    lineHeight: "1rem"
  },
  driverLink: {
    "& svg": {
      transform: "rotateY(180deg)"
    }
  }
}));

const dateFns = new DateFnsUtils();

function RoutesToolbar(props) {
  const {
    organisation,
    solution,
    selectedRoutes,
    searchInputValue,
    setSearchInputValue,
    setSelectedRoutes,
    handleReoptimizeSelectedRoutes,
    published,
    handleRoutesLock,
    project,
    setLoadingRoutesList,
    isPastProject,
    shareRoute,
    hasSelectedRouteStops,
    setInfoDialog,
    isReloadAllowed,
    onSearchFocus,
    onSearchBlur,
    distanceUnits,
    setPodPhotoRequirementForRoutes
  } = props;
  // General
  const classes = useStyles();

  // States
  const [selectedLockedRoutes, setSelectedLockedRoutes] = useState([]);
  const [reoptimizeDisabled, setReoptimizeDisabled] = useState({
    disabled: false,
    reason: ""
  });
  const [isPrinting, setPrinting] = useState(false);
  const [reopenConfirmation, setReopenConfirmation] = useState(false);

  // Variables
  const routeGroups =
    Object.keys(solution?.routeGroups).length > 0
      ? solution.routeGroups[Object.keys(solution.routeGroups)[0]].filter(
        backhaulGroup => backhaulGroup.some(route => isNotEmptyRoute(route))
      )
      : [];
  const routeGroupsId = routeGroups.map(routeGroup => routeGroup[0].id);
  const routeGroupsData = routeGroups.map(routeGroup => ({
    mainRouteId: routeGroup[0].id,
    isSelected: selectedRoutes.includes(routeGroup[0].id),
    isLocked: routeGroup[0].locked
  }));
  const numSelected = routeGroupsData.reduce((currentCount, routeGroup) => {
    return routeGroup.isSelected ? currentCount + 1 : currentCount;
  }, 0);
  const allRoutesSelected = numSelected === routeGroupsId.length;

  const { plan } = organisation;

  const isFOJW = checkIsFOJW()
  // Refs
  const printRef = useRef();
  let readyToPrint = useRef(false);

  // Callbacks
  const handleSearchChange = searchInputValue => {
    setSearchInputValue(searchInputValue);
  };

  const selectAllRoutes = () => {
    setSelectedRoutes(
      numSelected === routeGroupsId.length
        ? []
        : solution.routes.filter(el => el.selected).map(el => el.id)
    );
  };

  const handleDownloadRoutes = () => {
    const header = [...headerRow];
    header.unshift("route", "vehicle", "driver", "stop", "eta");
    isPastProject &&
      header.push(
        "project_name",
        "project_date",
        "route_started_at",
        "route_completed_at",
        "delivery_status",
        "delivered_at",
        "external_note",
        "internal_note",
        distanceUnits === "km"
          ? "planned_distance_km"
          : "planned_distance_miles",
        "pod_age",
        "pod_signature",
        "pod_photo"
      );

    const newRoutes = isReloadAllowed
      ? mergeReloadStopsToMainRoute(solution.routes)
      : solution.routes;

    const data = [
      header,
      ...newRoutes.reduce((array, route) => {
        let count = 0;
        return selectedRoutes.includes(route.id)
          ? [
            ...array,
            ...route.stops
              .filter(
                task =>
                  !["start", "end", "break", "reload"].includes(task.type)
              )
              .reduce((array, row) => {
                count++;

                return [
                  ...array,
                  ...(!row.units || row.units.length === 0
                    ? [
                      generateStopDataForXLSX(row, 0, {
                        type: "routes",
                        isPastProject,
                        published,
                        route: route.index,
                        vehicle: route.vehicle_name,
                        driver: route.driver
                          ? `${route.driver.first_name} ${route.driver.last_name}`
                          : "",

                        stop: count,
                        project_name: project.name,
                        project_date: dateFns.format(
                          new Date(getDateValue(project.project_date)),
                          "dd-MM-yyyy"
                        ),
                        route_started_at: route.start_timestamp,
                        route_completed_at: route.end_timestamp,
                        planned_distance: distanceStr(
                          route.distances,
                          distanceUnits === "km",
                          true
                        )
                      })
                    ]
                    : row.units.map((unit, i) =>
                      generateStopDataForXLSX(row, i, {
                        type: "routes",
                        isPastProject,
                        published,
                        route: route.index,
                        vehicle: route.vehicle_name,
                        driver: route.driver
                          ? `${route.driver.first_name} ${route.driver.last_name}`
                          : "",

                        stop: count,
                        project_name: project.name,
                        project_date: dateFns.format(
                          new Date(getDateValue(project.project_date)),
                          "dd-MM-yyyy"
                        ),
                        route_started_at: route.start_timestamp,
                        route_completed_at: route.end_timestamp,
                        planned_distance: distanceStr(
                          route.distances,
                          distanceUnits === "km",
                          true
                        )
                      })
                    ))
                ];
              }, [])
          ]
          : array;
      }, [])
    ];
    exportToXlsx("project_solution.xlsx", data);
  };

  // Effects
  useEffect(() => {
    const lockedRoutes = solution
      ? solution.routes.filter(route => route.locked)
      : [];

    setSelectedLockedRoutes(
      lockedRoutes.filter(route => selectedRoutes.includes(route.id))
    );
  }, [selectedRoutes, solution]);

  useEffect(() => {
    if (solution && solution.routes) {
      let reason = "";
      let reoptimizeDisabled = false;
      if (!hasSelectedRouteStops) {
        reason = `Selected route${numSelected > 1 ? "s" : ""
          } has no stops to reoptimize.`;
        reoptimizeDisabled = true;
      } else {
        reoptimizeDisabled = selectedRoutes.some(routeId => {
          const route = solution.routes.find(route => route.id === routeId);
          if (!reason && route) {
            if (route.locked) {
              reason = "Reoptimizing locked vehicle not allowed";
            } else if (
              !plan.ft_adjust_live_routes &&
              route.status === "in_progress"
            ) {
              reason =
                "You can not reoptimize, as one of the routes is in progress.";
            }
          }
          return (
            route &&
            (route.locked ||
              (!plan.ft_adjust_live_routes && route.status === "in_progress"))
          );
        });
      }
      setReoptimizeDisabled({ disabled: reoptimizeDisabled, reason });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoutes, solution]);

  // Build list of stops from selected routes (for the POD photo toggle decision)
  const selectedRouteIdsForActions = selectedRoutes.filter(routeId =>
    solution && solution.routes ? solution.routes.some(r => r.id === routeId) : false
  );

  const selectedRouteStops = (solution && solution.routes)
    ? selectedRouteIdsForActions
      .map(id => solution.routes.find(route => route.id === id))
      .filter(Boolean)
      .flatMap(route => route.stops || [])
      .filter(stop => !["start", "end", "break"].includes(stop.type))
    : [];

  const hasAnyPodPhotoEnabledForSelectedRoutes = selectedRouteStops.some(
    stop => stop && stop.requirements && stop.requirements.picture
  );

  useEffect(() => {
    if (isPrinting && !readyToPrint.current) {
      readyToPrint.current = true;
    } else if (readyToPrint.current) {
      readyToPrint.current = false;
    }
  }, [isPrinting]);

  const routesSearchboxPlaceholder = "Search routes";

  // A route is reopenable when it shows as Finished — every customer stop is done — which is the
  // same signal the "Finished" status pill uses (getRouteListStatus). Do NOT key off route.status
  // here: while the project is being edited (status "optimized"), the loaded solution can carry a
  // non-"finished" route.status even though every stop is finished and the route is FINISHED in the
  // DB, which left this button wrongly disabled. Matching the pill keeps it consistent with what the
  // dispatcher sees, and such routes are FINISHED server-side so the reopen call acts on them.
  const reopenableRouteIds = (solution && solution.routes ? solution.routes : [])
    .filter(
      route =>
        selectedRoutes.includes(route.id) &&
        getRouteListStatus(route) === "finished"
    )
    .map(route => route.id);

  const handleReopenRoutes = () => {
    if (props.reopenRoutes) props.reopenRoutes(reopenableRouteIds);
  };

  return (
    <>
      <div className={classes.toolbarWrapper}>
        <Box overflow="hidden" width="100%">
          <SmallSearchBox
            isEnhancedSearch
            placeholder={routesSearchboxPlaceholder}
            showSearchField={true}
            className={classes.searchBox}
            searchText={searchInputValue}
            onSearchChanged={handleSearchChange}
            ariaLabel={routesSearchboxPlaceholder}
            dataCy="stops-search"
            setFocusedOuter={focused =>
              focused ? onSearchFocus() : onSearchBlur()
            }
          />
        </Box>
        {/*
        <IconButton
          className={clsx(classes.iconButton, classes.button)}
          variant="outlined"
        >
          <img src={filterIcon} alt="" />
        </IconButton>
        */}
      </div>
      {!!numSelected && (
        <div className={classes.toolbarActions}>
          <div className={classes.checkboxWrapper}>
            <OptimizedTooltip
              title={
                allRoutesSelected ? (
                  "Deselect all"
                ) : (
                  <>
                    Select all
                    {/* <span style={{ marginRight: "2px", marginLeft: "6px" }}>
                      ⌘
                    </span>
                    <span>A</span> */}
                  </>
                )
              }
              placement="bottom"
            >
              <Checkbox
                color="primary"
                indeterminate={!allRoutesSelected}
                checked={allRoutesSelected}
                onChange={selectAllRoutes}
              />
            </OptimizedTooltip>
            <Typography className={classes.selectedText}>
              {numSelected} selected
            </Typography>
          </div>
          <Box display={"flex"} alignItems={"center"}>
            {!published && (
              <>
                <OptimizedTooltip
                  placement="bottom"
                  title={
                    reoptimizeDisabled.disabled
                      ? reoptimizeDisabled.reason
                      : numSelected === 1
                        ? "Reoptimize route"
                        : "Reoptimize routes"
                  }
                >
                  <span>
                    <IconButton
                      onClick={handleReoptimizeSelectedRoutes}
                      className={classes.iconButton}
                      disabled={
                        reoptimizeDisabled.disabled || !hasSelectedRouteStops
                      }
                      color="primary"
                    >
                      <MagicIcon color="primary" />
                    </IconButton>
                  </span>
                </OptimizedTooltip>
                <Divider
                  flexItem
                  orientation="vertical"
                  style={{ margin: "0 4px " }}
                />
              </>
            )}
            {isFOJW && (
              <>
                {hasAnyPodPhotoEnabledForSelectedRoutes ? (
                  <OptimizedTooltip
                    placement="bottom"
                    title={`Disable POD photo for selected route${numSelected > 1 ? "s" : ""}`}
                  >
                    <span>
                      <IconButton
                        className={classes.iconButton}
                        onClick={() => setPodPhotoRequirementForRoutes(
                          selectedRoutes.filter(routeId => routeGroupsId.includes(routeId)),
                          false
                        )}
                      >
                        <NoPhotographyIcon />
                      </IconButton>
                    </span>
                  </OptimizedTooltip>
                ) : (
                  <OptimizedTooltip
                    placement="bottom"
                    title={`Enable POD photo for selected route${numSelected > 1 ? "s" : ""}`}
                  >
                    <span>
                      <IconButton
                        className={classes.iconButton}
                        onClick={() => setPodPhotoRequirementForRoutes(
                          selectedRoutes.filter(routeId => routeGroupsId.includes(routeId)),
                          true
                        )}
                      >
                        <PhotoCamera />
                      </IconButton>
                    </span>
                  </OptimizedTooltip>
                )}
              </>
            )}
            {!published && (
              <>
                <OptimizedTooltip
                  placement="bottom"
                  title={
                    selectedLockedRoutes.length > 0
                      ? `Unlock route${numSelected > 1 ? "s" : ""}`
                      : `Lock route${numSelected > 1 ? "s" : ""}`
                  }
                >
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => {
                      if (getCookie("dontShowLockDialog")) {
                        handleRoutesLock(
                          selectedRoutes.filter(routeId =>
                            routeGroupsId.includes(routeId)
                          ),
                          selectedLockedRoutes.length > 0 &&
                          selectedLockedRoutes.length !==
                          selectedRoutes.length
                        );
                      } else {
                        setInfoDialog({
                          visible: true,
                          checkBox: true,
                          title: "Lock route",
                          body: "You can lock routes to prevent them from being changed. For example, if you click on the reoptimize button, all the remaining routes will be reoptimized without changing your locked routes.",
                          action: () =>
                            handleRoutesLock(
                              selectedRoutes.filter(routeId =>
                                routeGroupsId.includes(routeId)
                              ),
                              selectedLockedRoutes.length > 0 &&
                              selectedLockedRoutes.length !==
                              selectedRoutes.length
                            )
                        });
                      }
                    }}
                  >
                    {selectedLockedRoutes.length > 0 ? (
                      <LockOpenIcon />
                    ) : (
                      <LockIcon />
                    )}
                  </IconButton>
                </OptimizedTooltip>
                <Divider
                  flexItem
                  orientation="vertical"
                  style={{ margin: "0 4px " }}
                />
              </>
            )}
            {numSelected === 1 &&
              (published || (!published && project.published_once)) && (
                <OptimizedTooltip
                  placement="bottom"
                  title={
                    !hasSelectedRouteStops
                      ? `Selected route has no stops to share.`
                      : "Share route"
                  }
                >
                  <span>
                    <IconButton
                      className={clsx(classes.iconButton, classes.driverLink)}
                      onClick={() => shareRoute(selectedRoutes)}
                      disabled={!hasSelectedRouteStops}
                    >
                      <ReplyIcon />
                    </IconButton>
                  </span>
                </OptimizedTooltip>
              )}
            <OptimizedTooltip
              placement="bottom"
              title={
                reopenableRouteIds.length
                  ? `Reopen finished route${reopenableRouteIds.length > 1 ? "s" : ""}`
                  : "Select a finished route to reopen"
              }
            >
              <span>
                <IconButton
                  className={classes.iconButton}
                  disabled={!reopenableRouteIds.length}
                  onClick={() =>
                    !reopenConfirmation && setReopenConfirmation(true)
                  }
                >
                  <RestoreIcon />
                </IconButton>
              </span>
            </OptimizedTooltip>

            {/* Print */}
            <ReactToPrint
              key={1}
              onBeforeGetContent={() => {
                setPrinting(true);
                setLoadingRoutesList(true);
                return new Promise((resolve, reject) => {
                  const interval = setInterval(() => {
                    if (readyToPrint.current) {
                      setLoadingRoutesList(false);
                      clearInterval(interval);
                      resolve();
                    }
                  }, 500);
                });
              }}
              pageStyle={`
                @media print {
                  body { 
                    -webkit-print-color-adjust: exact; 
                  }
                }
                @page { size: auto;  margin: 16mm 16mm 8mm 16mm; }
              `}
              onAfterPrint={() => setPrinting(false)}
              trigger={() => (
                <OptimizedTooltip
                  placement="bottom"
                  title={`Print route${numSelected > 1 ? "s" : ""}`}
                >
                  <span>
                    <IconButton
                      className={classes.iconButton}
                    //disabled={!hasSelectedRouteStops}
                    >
                      <PrintIcon />
                    </IconButton>
                  </span>
                </OptimizedTooltip>
              )}
              content={() => {
                return printRef.current;
              }}
            />
            <OptimizedTooltip
              placement="bottom"
              title={`Download route${numSelected > 1 ? "s" : ""}`}
            >
              <span>
                <IconButton
                  onClick={handleDownloadRoutes}
                  className={classes.iconButton}
                //disabled={!hasSelectedRouteStops}
                >
                  <SaveAltIcon />
                </IconButton>
              </span>
            </OptimizedTooltip>
          </Box>
        </div>
      )}
      <Box display="none">
        <Box ref={printRef}>
          {solution && isPrinting && (
            <PrintRoutes
              routes={solution.routes}
              selectedRoutes={selectedRoutes}
              projectName={project.name}
            />
          )}
        </Box>
      </Box>
      <ConfirmDialog
        isProject
        open={reopenConfirmation}
        handleClose={() => setReopenConfirmation(false)}
        action={handleReopenRoutes}
        confirmText="Reopen"
        title={`Reopen ${reopenableRouteIds.length} route${
          reopenableRouteIds.length > 1 ? "s" : ""
        }?`}
        body={`This returns the selected finished route${
          reopenableRouteIds.length > 1 ? "s" : ""
        } to in-progress so stops can be added again.`}
      />
    </>
  );
}

const mapStateToProps = state => ({
  project: state.project,
  billingDetails: state.account.billingDetails,
  organisation:
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation) ||
    {},
  selectedRoutes: state.common.selectedRoutes,
  solution: state.project.solution,
  published: ["published", "expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  isPastProject: ["expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  hasSelectedRouteStops: state.project.solution?.routes
    .filter(route => state.common.selectedRoutes.includes(route.id))
    .some(route =>
      route.stops.some(
        task =>
          task.type !== "start" &&
          task.type !== "end" &&
          !isEmptyObj(task) &&
          task.type !== "break"
      )
    ),
  isReloadAllowed: state.project.vrp.configuration.allow_reload,
  distanceUnits: state.project.vrp.configuration.distance_units
});

export default connect(mapStateToProps, {
  setInfoDialog,
  setSelectedRoutes,
  reoptimizeRoutes,
  handleRoutesLock,
  setLoadingRoutesList,
  reopenRoutes,
  setPodPhotoRequirementForRoutes
})(memo(RoutesToolbar));
